/**
 * uiSizes.ts
 *
 * Single source of truth for responsive sizes across the entire app.
 * Change a value here and every page that uses that token updates automatically.
 *
 * ─── How to use ───────────────────────────────────────────────────────────────
 *   import { ui } from '../config/uiSizes';
 *
 *   <h1 className={`font-extrabold text-gray-800 dark:text-gray-200 ${ui.text.pageTitle}`}>
 *   <input className={`rounded-lg border ${ui.input.base}`} />
 *   <button className={`rounded-lg font-semibold ${ui.button.md}`}>
 *   <div className={`flex items-center ${ui.gap.md}`}>
 *   <section className={ui.page.sectionPadding}>
 *
 * Each token is a full multi-breakpoint Tailwind string so JIT compiles correctly.
 * ──────────────────────────────────────────────────────────────────────────────
 */

// ─── Text sizes ───────────────────────────────────────────────────────────────
const text = {
  /** Main page titles  e.g. "Dashboard View", "Migration Setup" */
  lgTitle: 'text-4xl 2xl:text-5xl xl:text-4xl lg:text-3xl md:text-2xl',
  /** Main page titles  e.g. "Dashboard View", "Migration Setup" */
  pageTitle: 'text-3xl 2xl:text-3xl xl:text-2xl lg:text-2xl md:text-xl',
  /** Section / card headings  e.g. "Migrations & Snapshots" */
  sectionTitle: 'text-lg 2xl:text-lg xl:text-base lg:text-base md:text-sm',
  /** Sub-section headings  e.g. form section borders */
  subTitle: 'text-base 2xl:text-base xl:text-base lg:text-sm md:text-sm',
  /** Standard body copy */
  body: 'text-sm 2xl:text-sm xl:text-sm lg:text-sm md:text-xs',
  /** Secondary / helper text */
  xs: 'text-xs 2xl:text-xs xl:text-xs lg:text-[11px] md:text-[10px]',
  /** Very small fixed text — e.g. extra-tight secondary labels */
  xxs: 'text-[10px] 2xl:text-[10px] xl:text-[9px] lg:text-[8px] md:text-[7px]',
  /** Small fixed text — e.g. compact inline data, tight data cells */
  sm: 'text-[11px]  2xl:text-[11px] xl:text-[11px] lg:text-[10px] md:text-[9px]',
  /** Form labels */
  label: 'text-sm 2xl:text-sm xl:text-sm lg:text-xs md:text-xs',
  /** Legend / key labels  e.g. "Review button key" */
  legend: 'text-[11px] 2xl:text-[14px] xl:text-[13px] lg:text-[11px] md:text-[10px]',
  /** Table column headers */
  tableHeader: 'text-[13px] 2xl:text-[13px] xl:text-[12px] lg:text-[11px] md:text-[8px]',
  /** Table cell content */
  tableCell: 'text-[11px] 2xl:text-[14px] xl:text-[13px] lg:text-[12px] md:text-[9px]',
  /** Badge / pill text  e.g. "Overview", type tags */
  badge: 'text-xs 2xl:text-sm xl:text-xs lg:text-xs md:text-[10px]',
  /** Pagination control text — e.g. page numbers and prev/next labels */
  pagination: 'text-[11px] 2xl:text-[14px] xl:text-[13px] lg:text-[12px] md:text-[11px]',
  /** Modal dialog title */
  modalTitle: 'text-2xl 2xl:text-2xl xl:text-[1.7em] lg:text-[1.7em] md:text-[1.5em]',
  /** Day-picker circular button labels — e.g. MON, TUE */
  dayButtonLabel: 'text-xs 2xl:text-xs xl:text-xs lg:text-[10px] md:text-[10px]',
  /** Header brand/app name — e.g. "Genesys Migrator" */
  brandTitle: 'text-2xl 2xl:text-2xl xl:text-lg lg:text-2xl md:text-xl',
  /** Centered page title in header — e.g. "Snapshot Setup", "Activity Logs" */
  headerPageTitle: 'text-xl 2xl:text-4xl xl:text-3xl lg:text-3xl md:text-2xl',
  /** Form section labels — e.g. "Migration Name:", "Source Organization", "Include Dependencies" */
  formSectionLabel: 'text-sm 2xl:text-base xl:text-base lg:text-base md:text-base',
  /** Bold form step/section headings — e.g. h3 headings within setup page steps */
  formStepTitle: 'text-lg 2xl:text-xl xl:text-xl lg:text-xl md:text-xl'
} as const;

// ─── Input / textarea ─────────────────────────────────────────────────────────
const input = {
  /** Standard input padding */
  base: 'px-4 py-2 2xl:px-4 2xl:py-2 xl:px-4 xl:py-2 lg:px-3 lg:py-1.5 md:px-2 md:py-1',
  /** Compact input  e.g. inline or within table rows */
  sm: 'px-3 py-1.5 2xl:px-3 2xl:py-1.5 xl:px-3 xl:py-1.5 lg:px-2 lg:py-1 md:px-2 md:py-0.5',
  /** Custom form input — e.g. setup page labeled form fields with moderate padding */
  custom: 'px-3 py-2 2xl:px-2 2xl:py-2 xl:px-2 xl:py-2 lg:px-1.5 lg:py-2 md:px-1.5 md:py-1',
  /** Form-row dropdown trigger — matches the native <select> sizing used in OrganizationSelector; flat px-3 py-2 at all breakpoints so all row inputs stay the same height */
  formDropdown: 'px-3 py-2',
  /** Modal form input — compact padding for inputs inside modal dialogs */
  modal: 'px-2 py-2 2xl:px-2 2xl:py-2 xl:px-2 xl:py-2 lg:px-1.5 lg:py-1.5 md:px-1 md:py-1',
  layout: 'pr-8 2xl:pr-8 xl:pr-6 lg:pr-4 md:pr-3' // for inputs with icons on the right side (e.g. clear button, dropdown arrow)
} as const;

// ─── Misc sizes ──────────────────────────────────────────────────────────────────
const size = {
  /** Modal view inner padding — generous vertical and horizontal padding for modal content */
  modalView: 'py-10 px-12 2xl:py-10 2xl:px-12 xl:py-8 xl:px-10 lg:py-6 lg:px-8 md:py-4 md:px-6'
} as const;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const button = {
  /** Regular action buttons */
  md: 'px-4 py-2 2xl:px-4 2xl:py-2 xl:px-4 xl:py-2 lg:px-3 lg:py-1.5 md:px-2 md:py-1 text-sm',
  /** Small / secondary buttons */
  sm: 'px-3 py-1 2xl:px-3 2xl:py-1 xl:px-3 xl:py-1 lg:px-2 lg:py-0.5 md:px-2 md:py-0.5 text-xs',
  /** Pagination buttons */
  pagination:
    'px-3 py-1 2xl:px-3 2xl:py-2 xl:px-3 xl:py-2 lg:px-2.5 lg:py-2 md:px-2 md:py-1 text-[11px] 2xl:text-[13px] xl:text-[12px] lg:text-[12px] md:text-[9px]',
  /** Large CTA buttons */
  lg: 'px-6 py-3 2xl:px-6 2xl:py-3 xl:px-5 xl:py-2.5 lg:px-4 lg:py-2 md:px-3 md:py-1.5 text-base',
  /** Rounded pill table buttons (View / Delete / Review / Attention) */
  pill: 'px-6 py-2 2xl:px-8 2xl:py-3 xl:px-6 xl:py-2 lg:px-4 lg:py-2 md:px-2 md:py-1 text-[11px] 2xl:text-[13px] xl:text-[12px] lg:text-[12px] md:text-[9px]',
  /** Modal confirm/cancel buttons — prominent padding with responsive text size */
  modalButton:
    'px-8 py-3 2xl:px-8 2xl:py-3.5 xl:px-6 xl:py-3 lg:px-4 lg:py-2 md:px-4 md:py-3 text-base 2xl:text-base xl:text-sm lg:text-sm md:text-xs',
  /** Filter-bar action buttons e.g. Download, Approve & import */
  filterBar:
    'whitespace-nowrap px-4 py-2 2xl:px-4 2xl:py-2 xl:px-4 xl:py-2 lg:px-3 lg:py-2 md:px-2 md:py-1.5 text-sm lg:text-sm md:text-xs',
  /** Header navigation buttons — e.g. Capture Snapshot, Start Migration, Logout, Back to Dashboard */
  header:
    'px-2 py-1 2xl:px-5 2xl:py-2 xl:px-4 xl:py-2 lg:px-3 lg:py-2 md:px-2.5 md:py-1.5 text-xs 2xl:text-base xl:text-sm lg:text-sm md:text-xs',
  /** Wide CTA buttons — extra horizontal padding, normal vertical height (same as md) */
  wide: 'px-8 py-2 2xl:px-8 2xl:py-2 xl:px-7 xl:py-2 lg:px-5 lg:py-1.5 md:px-4 md:py-1 text-base 2xl:text-base xl:text-sm lg:text-sm md:text-sm',
  /** Copy error log button — responsive sizing for error log copy action */
  copyError: 'px-4 py-2 2xl:px-4 2xl:py-2 xl:px-3.5 xl:py-1.5 lg:px-3 lg:py-1.5 md:px-3 md:py-1.5 text-sm 2xl:text-sm xl:text-sm lg:text-xs md:text-xs'
} as const;

// ─── badge ─────────────────────────────────────────────────
const badge = {
  /** Small badge with text — e.g. status/type tags combining padding and text size */
  sm: 'px-2 py-0.5 2xl:px-2 2xl:py-0.5 xl:px-2 xl:py-0.5 lg:px-1.5 lg:py-0.5 md:px-1.5 md:py-0.5 text-xs 2xl:text-sm xl:text-xs lg:text-xs md:text-[10px]',
  /** Count / label pill  e.g. resource type count badge */
  padding: 'px-2.5 py-1 2xl:px-2.5 2xl:py-1 xl:px-2 xl:py-0.5 lg:px-2 lg:py-0.5 md:px-1.5 md:py-0.5'
};

// ─── Page / layout padding ────────────────────────────────────────────────────
const page = {
  /** Outermost page wrapper  e.g. the root <div> of each page */
  padding: 'p-6 2xl:p-6 xl:p-4 lg:p-2 md:p-0.5',
  /** Large page wrapper padding — e.g. spacious page containers with extra breathing room */
  lgPadding: 'p-8 2xl:p-8 xl:p-6 lg:p-6 md:p-4',
  /** Section / panel  e.g. the gray rounded section card */
  sectionPadding: 'p-4 2xl:p-4 xl:p-4 lg:p-4 md:p-2',
  /** White content card  e.g. form containers */
  cardPadding: 'p-6 xl:p-5 lg:p-4 md:p-3',
  /** List / accordion row  e.g. summary rows in resource tables */
  rowPadding: 'px-5 py-3 2xl:px-5 2xl:py-3 xl:px-4 xl:py-2.5 lg:px-3 lg:py-2 md:px-2.5 md:py-1.5',
  /** Dashboard table data cell padding (type, createdBy, sourceOrg, targetOrg, lastRun columns) */
  dashboardCell: 'px-3 py-1 2xl:px-2 2xl:py-2 xl:px-2 xl:py-2 lg:px-1 lg:py-2 md:px-1.5 md:py-0.5',
  /** Standard table cell / header padding e.g. log table rows */
  tableCell: 'px-4 py-3 2xl:px-4 2xl:py-3 xl:px-4 xl:py-3 lg:px-3 lg:py-2.5 md:px-2 md:py-2',
  /** Name column inner content box padding e.g. dashboard name cell pill */
  nameCellContent:
    'px-2 py-2 2xl:px-2 2xl:py-2 xl:px-2 xl:py-2 lg:px-1.5 lg:py-2 md:px-1.5 md:py-2',
  /** Status icon column cell padding */
  statusCell: 'px-3 py-1 2xl:px-3 2xl:py-2 xl:px-2 xl:py-2 lg:px-1 lg:py-1 md:px-1 md:py-0.5',
  /** Actions column cell padding */
  actionCell: 'px-2 py-2',
  /** Full-width block/panel vertical padding  e.g. dashed add-item buttons */
  blockPy: 'py-4 2xl:py-4 xl:py-4 lg:py-3 md:py-2',
  /** Small all-sides padding  e.g. scrollable inner containers */
  smPadding: 'p-3 2xl:p-3 xl:p-2.5 lg:p-1.5 md:p-1',

  /** Modal close (×) button padding — uniform p-2 at all breakpoints */
  modalCloseButton: 'p-2 2xl:p-2 xl:p-2 lg:p-2 md:p-2',
  /** User info strip container padding */
  userInfoStrip: 'px-6 py-2 2xl:px-6 2xl:py-2 xl:px-5 xl:py-1.5 lg:px-4 lg:py-1 md:px-3 md:py-1',
} as const;

// ─── Flex / grid gaps ─────────────────────────────────────────────────────────
const gap = {
  /** Tight  e.g. icon+label pairs, checkbox+label */
  xs: 'gap-1.5 2xl:gap-1.5 xl:gap-1.5 lg:gap-1 md:gap-1',
  /** Small  e.g. form field rows, inline controls */
  sm: 'gap-2 2xl:gap-2 xl:gap-2 lg:gap-1.5 md:gap-1',
  /** Medium  e.g. general layout, button groups */
  md: 'gap-3 2xl:gap-3 xl:gap-2.5 lg:gap-2 md:gap-1.5',
  /** Large  e.g. card grids, major page sections */
  lg: 'gap-4 2xl:gap-4 xl:gap-3 lg:gap-2.5 md:gap-2',
  /** Extra large  e.g. transformation list, major stacked sections */
  xl: 'gap-6 2xl:gap-6 xl:gap-5 lg:gap-4 md:gap-3',
  /** Wide horizontal column gap — e.g. two-column page layouts */
  layout: 'gap-x-16 2xl:gap-x-16 xl:gap-x-12 lg:gap-x-8 md:gap-x-4',
  /** Compact — e.g. icon+name pairs inside name-cell content boxes */
  compact: 'gap-2 2xl:gap-1.5 xl:gap-1.5 lg:gap-1 md:gap-1',
  /** Button group grid — e.g. action button pairs in table rows */
  buttonGrid: 'gap-3 2xl:gap-4 xl:gap-3 lg:gap-2 md:gap-1',
  /** Day-picker button group — e.g. SchedulePicker day toggles */
  dayPicker: 'gap-1.5 2xl:gap-1.5 xl:gap-2.5 lg:gap-1 md:gap-1',
  /** Header left area gap (logo + brand name) */
  headerLogo: 'gap-1.5 2xl:gap-3 xl:gap-3 lg:gap-3 md:gap-2',
  /** Header right area gap (action buttons) */
  headerButtons: 'gap-1 2xl:gap-3 xl:gap-2.5 lg:gap-2 md:gap-1.5'
} as const;

// ─── Margin bottom ────────────────────────────────────────────────────────────
const mb = {
  /** After headings within a section */
  title: 'mb-4 xl:mb-3 lg:mb-2 md:mb-1',
  /** After search bars / filter rows */
  control: 'mb-5 xl:mb-4 lg:mb-3 md:mb-2',
  /** After top-level section headers */
  section: 'mb-6 xl:mb-5 lg:mb-4 md:mb-3',
  /** Large margin-bottom — e.g. after major section or page heading blocks */
  lg: 'mb-8 xl:mb-7 lg:mb-6 md:mb-4',
  /** Small margin-bottom — e.g. tight spacing between compact stacked items */
  sm: 'mb-2 2xl:mb-2 xl:mb-2 lg:mb-1.5 md:mb-1'
} as const;

// ─── Widths & heights ─────────────────────────────────────────────────────────
const widthAndHeight = {
  /** Standard fixed width for buttons, inputs, etc. */
  standard: 'w-32',
  /** Standard icon container — e.g. icon tiles in stat/summary cards */
  base: 'w-12 h-12 2xl:w-12 2xl:h-12 xl:w-10 xl:h-10 lg:w-10 lg:h-10 md:w-10 md:h-10',
  /** Large icon container — e.g. featured or prominent icon tiles */
  lg: 'w-14 h-14 2xl:w-14 2xl:h-14 xl:w-14 xl:h-14 lg:w-12 lg:h-12 md:w-11 md:h-11',
  /** Modal dialog panel — responsive max-width and max-height constraints */
  modal:
    'w-full max-w-2xl max-h-[90vh] 2xl:max-w-2xl 2xl:max-h-[90vh] xl:max-w-xl xl:max-h-[90vh] lg:max-w-lg lg:max-h-[90vh] md:max-w-md md:max-h-[90vh]',
  /** Tooltip trigger button (the circular "i" button) */
  tooltipTrigger: 'w-4 h-4 2xl:w-4 2xl:h-4 xl:w-4 xl:h-4 lg:w-3.5 lg:h-3.5 md:w-3.5 md:h-3.5',
  /** Circular icon container inside the tooltip panel */
  iconContainer: 'w-8 h-8 2xl:w-8 2xl:h-8 xl:w-7 xl:h-7 lg:w-6 lg:h-6 md:w-6 md:h-6',
  /** Small icon container e.g. section-header icon boxes */
  iconContainerSm: 'w-6 h-6 2xl:w-6 2xl:h-6 xl:w-6 xl:h-6 lg:w-5 lg:h-5 md:w-5 md:h-5',
  /** Large icon container e.g. modal stat-tile icon boxes */
  iconContainerLg: 'w-9 h-9 2xl:w-9 2xl:h-9 xl:w-8 xl:h-8 lg:w-7 lg:h-7 md:w-6 md:h-6',
  /** Tooltip panel width */
  tooltipPanel: 'w-72 2xl:w-72 xl:w-64 lg:w-60 md:w-52',
  /** Hover preview image modal — responsive width for screenshot previews at different breakpoints */
  hoverPreview: 'w-[1000px] 2xl:w-[1000px] xl:w-[850px] lg:w-[850px] md:w-[700px]',
  /** Day-picker circular toggle buttons */
  dayButton: 'w-11 h-11 2xl:w-11 2xl:h-11 xl:w-11 xl:h-11 lg:w-8 lg:h-8 md:w-8 md:h-8',
  /** Time-picker hour / minute select dimensions */
  selectTime: 'w-14 h-10 2xl:w-14 2xl:h-10 xl:w-14 xl:h-10 lg:w-12 lg:h-9 md:w-12 md:h-8',
  /** Time-picker AM/PM select dimensions */
  selectAmPm: 'w-16 h-10 2xl:w-16 2xl:h-10 xl:w-16 xl:h-10 lg:w-14 lg:h-9 md:w-14 md:h-8',
  /** Header logo height (width is w-auto) */
  logoHeight: 'h-8 2xl:h-14 xl:h-12 lg:h-11 md:h-10',
  /** User info strip avatar circle */
  userInfoAvatar: 'w-6 h-6 2xl:w-6 2xl:h-6 xl:w-5 xl:h-5 lg:w-5 lg:h-5 md:w-4 md:h-4',
  /** Status icon — normal/passing state */
  statusIcon: 'h-9 w-9 2xl:h-9 2xl:w-9 xl:h-9 xl:w-9 lg:h-8 lg:w-8 md:h-6 md:w-6',
  /** Status icon — failed/error state (slightly taller for visual emphasis) */
  statusIconFailed: 'h-10 w-9 2xl:h-10 2xl:w-9 xl:h-11 xl:w-10 lg:h-9 lg:w-8 md:h-7 md:w-6',
  /** Scrollable history/table container inside a modal */
  scrollableHistory:
    'max-h-[320px] 2xl:max-h-[320px] xl:max-h-[280px] lg:max-h-[260px] md:max-h-[240px] overflow-y-scroll',
  /** Error log pre block inside the attention modal */
  errorLog: 'max-h-24 2xl:max-h-44 xl:max-h-40 lg:max-h-36 md:max-h-28',
  /** Snapshot comparison permission policy columns grid layout */
  comparisonGrid: 'grid-cols-1 xl:grid-cols-2',
  /** Snapshot comparison permission policy column scrollable height */
  comparisonColumnHeight: 'max-h-[500px] md:max-h-[300px]'
} as const;

// ─── Svg sizes ─────────────
const svg = {
  /** Standard icon size for buttons and inputs */
  sm: 'w-5 h-5 2xl:w-5 2xl:h-5 xl:w-4 xl:h-4 lg:w-4 lg:h-4 md:w-3.5 md:h-3.5',
  /** Medium SVG icon — e.g. section/card decorative icons */
  md: 'w-7 h-7 2xl:w-7 2xl:h-7 xl:w-6 xl:h-6 lg:w-5 lg:h-5 md:w-5 md:h-5',
  /** Extra-large SVG icon — e.g. hero/empty-state illustration icons */
  xl: 'w-10 h-10 2xl:w-10 2xl:h-10 xl:w-9 xl:h-9 lg:w-8 lg:h-8 md:w-7 md:h-7',
  /** Large SVG icon — e.g. feature/page section header icons */
  lg: 'w-6 h-6 2xl:w-6 2xl:h-6 xl:w-5 xl:h-5 lg:w-4 lg:h-4 md:w-3.5 md:h-3.5',
  /** Extra-small icon  e.g. the "i" dot inside the Tooltip trigger button */
  xxs: 'w-2.5 h-2.5 2xl:w-2.5 2xl:h-2.5 xl:w-2.5 xl:h-2.5 lg:w-2 lg:h-2 md:w-2 md:h-2',
  /** Extra-extra-small icon  e.g. cron schedule icon in dashboard table rows */
  xs: 'w-4 h-4 2xl:w-5 2xl:h-5 xl:w-3.5 xl:h-3.5 lg:w-3.5 lg:h-3.5 md:w-2.5 md:h-2.5',
  /** Copy error button icon — matches copyError button sizing */
  copyError: 'w-4 h-4 2xl:w-4 2xl:h-4 xl:w-4 xl:h-4 lg:w-3.5 lg:h-3.5 md:w-3.5 md:h-3.5'
} as const;

// ─── Root export ──────────────────────────────────────────────────────────────
/** Global responsive UI size tokens. Import `ui` and use like: className={ui.text.body} */
export const ui = { text, input, button, page, gap, mb, svg, size, badge, widthAndHeight } as const;

// ─────────────────────────────────────────────────────────────────────────────
// Table layout types & configs (used by useTableLayout hook)
// ─────────────────────────────────────────────────────────────────────────────

export type Breakpoint = '2xl' | 'xl' | 'lg' | 'md';

export interface BreakpointSizes {
  /** Column widths in px — array index matches <colgroup> order */
  colWidths: number[];
}

export interface TableLayoutConfig {
  breakpoints: Record<Breakpoint, BreakpointSizes>;
  /** Tailwind class string for <th> text size — defaults to ui.text.tableHeader */
  headerTextSizeClass: string;
  /** Tailwind class string for <td> text size — defaults to ui.text.tableCell */
  cellTextSizeClass: string;
}

// ─── Dashboard table ──────────────────────────────────────────────────────────
//  Columns: [0] Name  [1] Type  [2] CreatedBy  [3] SourceOrg  [4] TargetOrg
//           [5] LastRun  [6] Status  [7] Actions
export const dashboardTableConfig: TableLayoutConfig = {
  breakpoints: {
    '2xl': { colWidths: [240, 80, 120, 90, 90, 120, 65, 210] },
    xl: { colWidths: [200, 65, 80, 90, 90, 85, 60, 170] },
    lg: { colWidths: [180, 65, 80, 65, 65, 80, 56, 170] },
    md: { colWidths: [130, 65, 60, 55, 55, 75, 50, 150] }
  },
  headerTextSizeClass: ui.text.tableHeader,
  cellTextSizeClass: ui.text.tableCell
};

// ─── Add table configs for other pages here ───────────────────────────────────
// export const snapshotTableConfig: TableLayoutConfig = {
//   breakpoints: {
//     '2xl': { colWidths: [300, 150, 200] },
//     ...
//   },
//   headerTextSizeClass: ui.text.tableHeader,
//   cellTextSizeClass:   ui.text.tableCell,
// };
